import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Loader2, Plus, Trash2 } from 'lucide-react';

const KINDS = [
  { value: 'closed', label: 'Closed', hint: 'The landlord has shut the kitchen that day.' },
  { value: 'replaced', label: 'Replaced hours', hint: 'Different hours from the usual agreement.' },
  { value: 'extra', label: 'Extra hours', hint: 'Additional hours granted on request, on top of the usual.' },
];

// A shift can never be booked outside availability by exception — extra hours
// granted by the landlord are recorded here instead.
const OverrideDialog = ({ open, kitchen, override, date, onClose, onSubmit, busy }) => {
  const [kind, setKind] = useState('closed');
  const [dateStr, setDateStr] = useState('');
  const [reason, setReason] = useState('');
  const [windows, setWindows] = useState([{ start_time: '06:00', end_time: '12:00' }]);

  useEffect(() => {
    if (!open) return;
    setKind(override?.kind || 'closed');
    setDateStr(override?.date || date || '');
    setReason(override?.reason || '');
    setWindows(
      override?.windows?.length
        ? override.windows.map((w) => ({ ...w }))
        : [{ start_time: '06:00', end_time: '12:00' }]
    );
  }, [open, override, date]);

  const setWindow = (index, patch) =>
    setWindows((prev) => prev.map((w, i) => (i === index ? { ...w, ...patch } : w)));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="override-dialog">
        <DialogHeader>
          <DialogTitle>Day override</DialogTitle>
          <DialogDescription>
            A one-day change to {kitchen?.name}&apos;s access — a closure, replaced hours, or extra hours.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="override-date">Date</Label>
            <Input
              id="override-date" type="date" value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              data-testid="override-date"
            />
          </div>

          <div>
            <Label>What changed</Label>
            <RadioGroup value={kind} onValueChange={setKind} className="mt-2 space-y-2">
              {KINDS.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
                  data-testid={`override-kind-${option.value}`}
                >
                  <RadioGroupItem value={option.value} className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                    <span className="block text-xs text-gray-500">{option.hint}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </div>

          {kind !== 'closed' && (
            <div>
              <div className="flex items-center justify-between">
                <Label>Hours on this day</Label>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setWindows((prev) => [...prev, { start_time: '06:00', end_time: '12:00' }])}
                  data-testid="override-add-window"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              <div className="mt-2 space-y-2">
                {windows.map((w, index) => (
                  <div key={index} className="flex items-end gap-2" data-testid={`override-window-${index}`}>
                    <div>
                      <Label className="text-xs text-gray-500">Opens</Label>
                      <Input
                        type="time" className="mt-1 w-[7.5rem]" value={w.start_time}
                        onChange={(e) => setWindow(index, { start_time: e.target.value })}
                        data-testid={`override-window-start-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Closes</Label>
                      <Input
                        type="time" className="mt-1 w-[7.5rem]" value={w.end_time}
                        onChange={(e) => setWindow(index, { end_time: e.target.value })}
                        data-testid={`override-window-end-${index}`}
                      />
                    </div>
                    {windows.length > 1 && (
                      <Button
                        type="button" variant="ghost" size="icon" className="mb-1 text-red-600"
                        onClick={() => setWindows((prev) => prev.filter((_, i) => i !== index))}
                        aria-label="Remove hours"
                        data-testid={`override-remove-window-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="override-reason">Reason (optional)</Label>
            <Input
              id="override-reason" value={reason} placeholder="e.g. landlord maintenance"
              onChange={(e) => setReason(e.target.value)}
              data-testid="override-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="override-cancel">Cancel</Button>
          <Button
            onClick={() => onSubmit({ date: dateStr, kind, reason, windows })}
            disabled={busy || !dateStr}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="override-save"
          >
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OverrideDialog;
